import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Box, Text } from '@rsos/base-components';
import { normalizeInlineRADE } from '@rsos/utils/normalizeRADE';
import fetchEmergencySession from '../../actions/fetchEmergencySession';
import { selectProductToken } from '../../utils/getProductToken';
import { selectLatestDataset } from '../Map/useDeckGL/EagleEye/helpers';
import TextTo911Card from '../TextTo911/TextTo911Card';
import AdrQueryContainer from './AdrQuery/AdrQueryContainer';
import EENCamerasBanner from './EENCamerasBanner';
import EllingtonCard from './EllingtonCard';
import selectADRForCall from './EllingtonCard/selectADRForCall';

const StyledEllingtonCardList = styled.div`
  overflow-y: auto;
`;

const ScrollableArea = styled.div`
  padding: ${({ noPadding }) => (noPadding ? '0px;' : '16px;')};
`;

export const EllingtonCardList = ({
  onZoom,
  onActionableButton,
  noPadding = false,
  summarized = false,
  onClick = () => {},
  isUniteEventPaneEnabled = false,
}) => {
  const dispatch = useDispatch();

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const eenLatestDataset = useSelector(selectLatestDataset);

  const isCamerasAvailable =
    selectedCallerID &&
    eenLatestDataset &&
    parseInt(selectedCallerID) === eenLatestDataset.topic;

  const additionalDataProps = useSelector(
    state => {
      const additionalData = selectADRForCall(state, selectedCallerID);
      const dataProps = []; // where a key item is an array path 'apple.0', 'apple.1', 'sxm.0'
      if (!additionalData) {
        return dataProps;
      }
      Object.keys(additionalData).forEach(org => {
        Object.keys(additionalData[org]).forEach(idx => {
          if (org === 'rad') {
            return;
          }

          const { display_schema, display_schema_url, dataCategory } =
            additionalData[org][idx];

          // The display schema should always exist at this point, leaving this
          // guard clause to match what was here before.
          if (!display_schema && !display_schema_url) {
            return;
          }

          const showDisclaimer =
            org === 'apple' ||
            org === 'emergency_health_association' ||
            (org === 'google' && dataCategory === 'car_crash');

          // As of 5.8.0, hide Avive ADR completely from the call pane.
          let hidden = false;
          if (org === 'avive') {
            hidden = true;
          }

          dataProps.push({
            org,
            showDisclaimer,
            path: [org, idx].join('.'),
            hidden,
          });
        });
      });
      return dataProps;
    },
    (prev, current) => JSON.stringify(prev) === JSON.stringify(current),
  );

  const handleFetchDataLink = useCallback(
    props => {
      const { mafid, schema = {} } = props;
      return new Promise((resolve, reject) => {
        (async () => {
          const { attributes = {} } = schema;
          try {
            const { payload } = await dispatch(
              fetchEmergencySession(mafid, {
                attributes,
                silentUpdate: true,
              }),
            );
            resolve(normalizeInlineRADE(payload));
          } catch (action) {
            reject(action.error || action);
          }
        })();
      });
    },
    [dispatch],
  );

  let allADR = additionalDataProps.sort((a, b) => {
    if (a.org === 'apple') {
      return -1;
    }
    if (b.org === 'apple') {
      return 1;
    }
    if (a.org === '911_inform') {
      return -1;
    }
    if (b.org === '911_inform') {
      return 1;
    }
    return 0;
  });

  const { productToken = '' } = useSelector(selectProductToken);

  return (
    <StyledEllingtonCardList data-name="adr-card-list">
      {!isUniteEventPaneEnabled && <AdrQueryContainer />}
      {isCamerasAvailable && !isUniteEventPaneEnabled && (
        <Box mx="16px" mt="16px">
          <EENCamerasBanner />
        </Box>
      )}
      <ScrollableArea noPadding={noPadding}>
        <TextTo911Card />
        {allADR.length === 0 && isUniteEventPaneEnabled && !summarized && (
          <Text color="secondaryText" size="normal" textAlign="center" mt="8px">
            No additional data available
          </Text>
        )}
        {allADR.map(({ showDisclaimer, path, org, hidden }) => {
          return !hidden ? (
            <React.Fragment key={`${org}-${path}`}>
              <EllingtonCard
                key={path}
                dataPath={path}
                selectedCallerID={selectedCallerID}
                showDisclaimer={showDisclaimer}
                source={org}
                onZoom={onZoom}
                onFetchDataLink={handleFetchDataLink}
                onActionableButton={onActionableButton}
                onClick={onClick}
                showExitButton={false}
                accessToken={productToken}
                summarized={summarized}
              />
            </React.Fragment>
          ) : null;
        })}
      </ScrollableArea>
    </StyledEllingtonCardList>
  );
};

export default EllingtonCardList;
